import { useRef, useState } from "react";
import { useLocation, Link } from "wouter";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { toast } from "@/lib/toast";
import { useAuth } from "@/hooks/useAuth";
import { useUploadContext } from "@/contexts/UploadContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  DEFAULT_SUCCESS_GRAPHIC,
  fileToSuccessGraphicDataUrl,
} from "@/lib/successGraphic";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LogOut, ChevronDown, AlertTriangle, Moon, Sun, Image as ImageIcon, Upload, RotateCcw, Loader2 } from "lucide-react";
import DriveStatusIndicator from "@/components/DriveStatusIndicator";
import { useTheme } from "@/contexts/ThemeContext";

/**
 * Shared site header with persistent navigation.
 * Renders the logo + brand on the left and Upload / Tag Libraries nav tabs below.
 * The active tab is highlighted based on the current route.
 * Shows a confirmation dialog before logging out if an upload is in progress.
 */
export default function AppHeader() {
  const [location] = useLocation();
  const { user, logout } = useAuth();
  const { isUploadActive } = useUploadContext();
  const { isDark, toggle: toggleTheme } = useTheme();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Self-service success-graphic picker (the corner dropdown → dialog).
  const utils = trpc.useUtils();
  const [showGraphicDialog, setShowGraphicDialog] = useState(false);
  const [savingGraphic, setSavingGraphic] = useState(false);
  const graphicInputRef = useRef<HTMLInputElement>(null);
  const setGraphic = trpc.account.setSuccessGraphic.useMutation({
    onSuccess: () => {
      utils.auth.me.invalidate();
    },
    onError: (err) => toast.error(`Couldn't save graphic: ${err.message}`),
  });

  async function handleGraphicFile(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setSavingGraphic(true);
    try {
      const dataUrl = await fileToSuccessGraphicDataUrl(file);
      await setGraphic.mutateAsync({ graphicUrl: dataUrl });
      toast.success("Success graphic updated.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not process image");
    } finally {
      setSavingGraphic(false);
    }
  }

  async function resetGraphic() {
    setSavingGraphic(true);
    try {
      await setGraphic.mutateAsync({ graphicUrl: null });
      toast.success("Reset to the default graphic.");
    } finally {
      setSavingGraphic(false);
    }
  }

  // The Confirmation page is shared by both workflows; the Provider A-Roll
  // variant carries `?kind=provider` in the URL. Route the active-tab
  // highlight by that signal so finishing a Provider A-Roll session lights
  // up "Provider A-Roll" instead of "Patient Footage". Wouter's
  // `useLocation` only returns the pathname, so we read the query string
  // off `window.location` directly — fresh on every render, same pattern
  // the Confirmation page itself uses.
  const onConfirmation = location.startsWith("/confirmation");
  const confirmationKind =
    onConfirmation && typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("kind")
      : null;
  const isProviderConfirmation = confirmationKind === "provider";

  const isUpload = location === "/" || (onConfirmation && !isProviderConfirmation);
  const isProviderARoll = location === "/provider-a-roll" || isProviderConfirmation;
  const isTags = location === "/tags";
  const isBoards = location === "/boards";
  const isFootageSearch = location === "/footage-search";
  const isRecents = location === "/recents";
  const isGuide = location === "/guide";
  const isAudit = location === "/audit";
  const isTeamAccess = location === "/authorized-accounts";
  const isAgentAccess = location === "/agent-access";
  const isReports = location === "/reports";
  const isWeeklyClips = location === "/weekly-clips";
  const isLibraryStats = location === "/library-stats";
  const isAdminSection =
    isAudit || isTeamAccess || isAgentAccess || isReports || isWeeklyClips || isLibraryStats;

  // Admin sub-menu can be toggled open by clicking "Admin Menu", and is
  // auto-revealed whenever the user is already on an admin page so they
  // can navigate between the sibling pages without re-opening it.
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const showAdminSubrow = user?.role === "admin" && (adminMenuOpen || isAdminSection);

  const tabClass = (active: boolean) =>
    cn(
      "px-4 py-3 text-sm font-medium border-b-2 transition-colors",
      active
        ? "text-primary border-primary"
        : "text-muted-foreground border-transparent hover:text-foreground hover:border-border"
    );

  const handleLogoutClick = () => {
    if (isUploadActive()) {
      // Upload is in progress — show confirmation dialog first
      setShowLogoutConfirm(true);
    } else {
      logout();
    }
  };

  return (
    <>
      {/* Brand bar */}
      <header className="bg-background border-b border-border shadow-sm print-hidden">
        <div className="container py-4 flex items-center gap-4">
          <Link href="/" className="flex items-center gap-3 no-underline">
            <img
              src="/icons/icon-192.png"
              alt="Footage Manager"
              className="w-10 h-10 rounded-xl"
            />
            <div>
              <h1 className="text-lg font-semibold text-foreground leading-tight">
                Footage Manager
              </h1>
              <p className="text-xs text-muted-foreground">Patient Footage Upload System</p>
            </div>
          </Link>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Drive status indicator */}
          {user && <DriveStatusIndicator />}

          {/* User menu */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {/* Avatar initial */}
                  <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {(user.name ?? user.googleId ?? "U").charAt(0).toUpperCase()}
                  </span>
                  <span className="hidden sm:block font-medium text-foreground max-w-[160px] truncate">
                    {user.name ?? user.googleId}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-3 py-2">
                  <p className="text-xs text-muted-foreground">Signed in as</p>
                  <p className="text-sm font-medium truncate">{user.name ?? user.googleId}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    // Keep the dropdown open after click so the user can see the
                    // theme flip without the menu unmounting first.
                    e.preventDefault();
                    toggleTheme();
                  }}
                  className="cursor-pointer"
                >
                  {isDark ? (
                    <>
                      <Sun className="w-4 h-4 mr-2" />
                      Light Mode
                    </>
                  ) : (
                    <>
                      <Moon className="w-4 h-4 mr-2" />
                      Dark Mode
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setShowGraphicDialog(true)}
                  className="cursor-pointer"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Success Graphic
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogoutClick}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>

      {/* Nav tabs */}
      <div className="bg-background border-b border-border print-hidden">
        <div className="container">
          <nav className="flex gap-0">
            <Link href="/" className={tabClass(isUpload)}>
              Patient Footage
            </Link>
            <Link href="/provider-a-roll" className={tabClass(isProviderARoll)}>
              Provider A-Roll
            </Link>
            <Link href="/footage-search" className={tabClass(isFootageSearch)}>
              Footage Search
            </Link>
            <Link href="/recents" className={tabClass(isRecents)}>
              Recents
            </Link>
            <Link href="/tags" className={tabClass(isTags)}>
              Tag Libraries
            </Link>
            <Link href="/boards" className={tabClass(isBoards)}>
              Boards
            </Link>
            <Link href="/guide" className={tabClass(isGuide)}>
              Guide
            </Link>
            {user?.role === "admin" && (
              <button
                type="button"
                onClick={() => setAdminMenuOpen((o) => !o)}
                className={cn(tabClass(isAdminSection), "inline-flex items-center gap-1")}
                aria-expanded={showAdminSubrow}
              >
                Admin Menu
                <ChevronDown
                  className={cn(
                    "w-3.5 h-3.5 transition-transform",
                    showAdminSubrow && "rotate-180"
                  )}
                />
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Admin sub-nav row */}
      {showAdminSubrow && (
        <div className="bg-muted/40 border-b border-border print-hidden">
          <div className="container">
            <nav className="flex gap-0">
              <Link href="/authorized-accounts" className={tabClass(isTeamAccess)}>
                Team Access
              </Link>
              <Link href="/agent-access" className={tabClass(isAgentAccess)}>
                Agent Access
              </Link>
              <Link href="/audit" className={tabClass(isAudit)}>
                Audit Log
              </Link>
              <Link href="/reports" className={tabClass(isReports)}>
                Reports
              </Link>
              <Link href="/weekly-clips" className={tabClass(isWeeklyClips)}>
                Weekly Clips
              </Link>
              <Link href="/library-stats" className={tabClass(isLibraryStats)}>
                Library Stats
              </Link>
            </nav>
          </div>
        </div>
      )}

      {/* Success graphic picker — self-service per-user celebration image */}
      <Dialog open={showGraphicDialog} onOpenChange={setShowGraphicDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your Success Graphic</DialogTitle>
            <DialogDescription>
              This appears on your success screens — the &ldquo;Logged and
              Loaded!&rdquo; page and the Footage Search &ldquo;bundle
              ready!&rdquo; view. Upload your own image or an animated GIF, or
              reset to the default.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center py-2">
            <img
              src={user?.successGraphicUrl ?? DEFAULT_SUCCESS_GRAPHIC}
              alt="Your success graphic preview"
              className="max-w-md max-h-[320px] w-auto h-auto rounded-xl shadow"
            />
          </div>
          {!user?.successGraphicUrl && (
            <p className="text-center text-xs text-muted-foreground">
              Currently showing the shared default graphic.
            </p>
          )}

          <input
            ref={graphicInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              void handleGraphicFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="ghost"
              disabled={savingGraphic || !user?.successGraphicUrl}
              onClick={() => void resetGraphic()}
              className="gap-1.5"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to Default
            </Button>
            <Button
              disabled={savingGraphic}
              onClick={() => graphicInputRef.current?.click()}
              className="gap-1.5"
            >
              {savingGraphic ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {savingGraphic ? "Saving…" : "Upload Image / GIF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logout confirmation dialog — shown only when an upload is in progress */}
      <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Upload in Progress
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                An upload session is currently active. Logging out now will interrupt the upload — files that have not finished uploading will <strong>not</strong> be saved to cloud storage or Google Drive.
              </span>
              <span className="block text-sm text-muted-foreground">
                Files already saved locally will not be affected.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay &amp; Continue Upload</AlertDialogCancel>
            <AlertDialogAction
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-600"
            >
              Log Out Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
